import { useEffect, useMemo } from 'react';
import { useHistory } from 'quartz-routing/react-router';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useCheckCaptionsExistQuery } from '../../../__generated__/chirp/com/hubspot/academy/transcripts/rpc/AcademyTranscriptsRpc.hooks';
import { VideoAssetTypename } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService';
import { VIDEO_TIMESTAMP_PARAM } from './videoConstants';
import useSearchParams from '../../hooks/useSearchParams';
export const useVideoParams = () => {
  const [searchParams] = useSearchParams();
  const startTime = searchParams[VIDEO_TIMESTAMP_PARAM];
  return {
    startTime: startTime ? Number(startTime) : undefined
  };
};
export const useAutoClearVideoVTS = () => {
  const {
    startTime
  } = useVideoParams();
  const history = useHistory();
  useEffect(() => () => {
    if (startTime) {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete(VIDEO_TIMESTAMP_PARAM);
      history.replace(Object.assign({}, history.location, {
        search: searchParams.toString()
      }));
    }
  }, [history, startTime]);
};
const videoPlayerConfigDefaultState = {
  autoPlay: false,
  playSpeed: 100
};
export const useVideoPlayerConfig = create()(persist(set => Object.assign({}, videoPlayerConfigDefaultState, {
  toggleAutoPlay() {
    set(state => ({
      autoPlay: !state.autoPlay
    }));
  },
  setPlaySpeed(speed) {
    set({
      playSpeed: speed
    });
  },
  setSubtitleLanguage(subtitleLanguage) {
    set({
      subtitleLanguage
    });
  }
}), {
  name: 'academy:videoPreferences'
}));
export const resetVideoPlayerConfig = () => useVideoPlayerConfig.setState(videoPlayerConfigDefaultState);
export const useVideoTimestamp = create(set => ({
  videoTimestamp: undefined,
  // Workaround: the video player triggers a seek only when the startTime prop
  // changes. Adding a tiny offset (1ms) forces a new prop value so repeated
  // clicks on the same timestamp still trigger a seek. Ideally the player would
  // expose an imperative seekTo(time) API instead of relying on prop changes.
  setVideoTimestamp: timestamp => set(state => ({
    videoTimestamp: state.videoTimestamp === undefined || state.videoTimestamp === timestamp ? timestamp + 0.001 : timestamp
  }))
}));
export const useAllVideosCaptionsExist = ({
  videoContentIds,
  options
}) => {
  const hasVideos = videoContentIds && videoContentIds.length > 0;
  const {
    data,
    loading,
    error
  } = useCheckCaptionsExistQuery(Object.assign({}, options, {
    variables: {
      contentIds: videoContentIds !== null && videoContentIds !== void 0 ? videoContentIds : []
    },
    skip: (options === null || options === void 0 ? void 0 : options.skip) || !hasVideos
  }));
  const allVideosHaveCaptions = useMemo(() => {
    if (!hasVideos || !data) return false;
    const existsMap = data.existsByContentId;
    return videoContentIds.every(id => existsMap[String(id)] === true);
  }, [data, hasVideos, videoContentIds]);
  return {
    allVideosHaveCaptions,
    captionsExistMap: data === null || data === void 0 ? void 0 : data.existsByContentId,
    loading: hasVideos && !(options !== null && options !== void 0 && options.skip) && loading,
    error
  };
};
export const useVideoContentCaptionCheck = ({
  contents
}) => {
  const videoContentIds = useMemo(() => {
    var _contents$filter$map;
    return (_contents$filter$map = contents === null || contents === void 0 ? void 0 : contents.filter(c => c.__typename === VideoAssetTypename).map(v => v.id)) !== null && _contents$filter$map !== void 0 ? _contents$filter$map : [];
  }, [contents]);
  return useAllVideosCaptionsExist({
    videoContentIds
  });
};